import { EConfigurationNames } from '@ConfigLoader/ConfigFiles';
import ConfigLoader from '@ConfigLoader/ConfigLoader';
import { isSpecialGamesTrigger } from '@Core/SpecialGames/SpecialGamesHelper';
import { EWinPresentationStates } from '@Core/Win/States/WinPresentationStates';
import { Game } from '@Game/Game';
import { gameInstance } from '@Game/GameComponent';
import { GameStore } from '@GameStore/GameStore';
import { EGameStoreKeys, ESoundSettingsKeys } from '@GameStore/GameStoreSchema';
import { ESpecialGamesType } from '@GFTypes/SpecialGames';
import EventsCenter from '@Managers/Events/EventsCenter';
import { ALL_EVENTS_LIST, EGameEvents } from '@Managers/Events/GameEvents';
import { Logger, LoggerLevel, LoggerSource } from '@Managers/Logger';
import { EGameStates } from '@Managers/StateManager/GameStates';
import { DeferredPromise } from '@Tools/DeferredPromise';
import { SoundManager } from './SoundManager';

export default class BackgroundSoundManager {
    public static readonly START_UP_SOUND_KEY: string = 'startup_sound';
    public static readonly BACKGROUND_SOUND_KEY: string = 'background_sound';
    public static readonly LOCK_AND_ROLL_BACKGROUND_SOUND_KEY: string = 'background_sound_lock_and_roll';
    public static readonly FREE_GAMES_BACKGROUND_SOUND_KEY: string = 'background_sound_free_games';

    private readonly BACKGROUND_SOUND_KEY_BY_GAME_TYPE = new Map<ESpecialGamesType, string>([
        [ESpecialGamesType.LOCK_AND_ROLL, BackgroundSoundManager.LOCK_AND_ROLL_BACKGROUND_SOUND_KEY],
        [ESpecialGamesType.FREE_GAMES, BackgroundSoundManager.FREE_GAMES_BACKGROUND_SOUND_KEY]
    ]);
    private readonly manager: SoundManager;
    private readonly gameStore: GameStore;
    private currentBackgroundSoundKey: string;
    private backgroundIntroInterruption: DeferredPromise<{ interrupted: boolean }>;

    constructor(game: Game, manager: SoundManager) {
        Logger.log(LoggerLevel.Constructor, {
            class: 'BackgroundSoundManager',
            message: 'BackgroundSoundManager constructor',
            source: LoggerSource.Game
        });
        this.manager = manager;
        this.gameStore = game.gameStore;
        this.currentBackgroundSoundKey = this.getBackgroundSoundKey();
        this.init();
    }

    init(): void {
        EventsCenter.on(ALL_EVENTS_LIST.GAME_START, this.playBackgroundSound, this);
        EventsCenter.on(ALL_EVENTS_LIST.PRE_WIN_STATE, this.pauseBackgroundSound, this);
        EventsCenter.on(EWinPresentationStates.PRE_WIN_PRESENTATION_STATE, this.pauseBackgroundSound, this);
        EventsCenter.on(EWinPresentationStates.POST_WIN_PRESENTATION_STATE, this.onCompleteWinPresentation, this);
        EventsCenter.on(EGameStates.SPIN_STATE, this.onSpin, this);
        EventsCenter.on(EGameStates.IDLE_STATE, this.onIdle, this);
        EventsCenter.on(EGameStates.PRE_SPECIAL_GAMES_INTRO_STATE, this.onPreSpecialGamesIntroState, this);
        EventsCenter.on(EGameStates.POST_SPECIAL_GAMES_INTRO_STATE, this.onPostSpecialGamesIntroState, this);
        EventsCenter.on(EGameEvents.SPECIAL_GAMES_MODE_EXIT, this.onPostSpecialGamesOutroState, this);
        this.gameStore.on(EGameStoreKeys.SoundSettings, this.onSoundSettingsChange, this);
    }

    private onSoundSettingsChange(): void {
        const isBackgroundEnabled = this.gameStore.get(EGameStoreKeys.SoundSettings).background;

        isBackgroundEnabled ? this.resumeBackgroundSound() : this.pauseBackgroundSound();
    }

    private isStartUpSoundPresent(): boolean {
        const config = ConfigLoader.getConfiguration(EConfigurationNames.SOUNDS);
        const isInSharedSounds = !!config.sharedSounds[BackgroundSoundManager.START_UP_SOUND_KEY];
        const isInGameSounds = !!config.gameSounds[BackgroundSoundManager.START_UP_SOUND_KEY];
        return isInSharedSounds || isInGameSounds;
    }

    private async playBackgroundSound(): Promise<void> {
        const isBackgroundEnabled = this.manager.soundSettingsManager.isSoundWithSettingEnabled(ESoundSettingsKeys.background);

        if (!isBackgroundEnabled) return;

        const backgroundSoundKey = this.getBackgroundSoundKey();

        if (!this.manager.isPlaying(backgroundSoundKey)) {
            this.manager.playSound(backgroundSoundKey, { loop: true });
        }

        if (this.isStartUpSoundPresent()) {
            this.pauseBackgroundSound();

            this.backgroundIntroInterruption = new DeferredPromise<{ interrupted: boolean }>();

            const startupSoundKey = BackgroundSoundManager.START_UP_SOUND_KEY;

            const result = await Promise.race([this.manager.waitForSoundComplete(startupSoundKey), this.backgroundIntroInterruption.promise]);

            if (result?.interrupted) {
                this.manager.stopSound(startupSoundKey);
                return;
            }

            this.resumeBackgroundSound();
        }
    }

    private pauseBackgroundSound(): void {
        this.manager.pauseSound(this.currentBackgroundSoundKey);
        if (this.manager.isPlaying(BackgroundSoundManager.START_UP_SOUND_KEY)) this.backgroundIntroInterruption?.resolve({ interrupted: true });
    }

    private resumeBackgroundSound(): void {
        const isBackgroundEnabled = this.gameStore.get(EGameStoreKeys.SoundSettings).background;
        if (!isBackgroundEnabled || isSpecialGamesTrigger(this.gameStore)) return;

        this.manager.resumeSound(this.currentBackgroundSoundKey);
    }

    private onCompleteWinPresentation(): void {
        if (gameInstance.gameStore.get(EGameStoreKeys.CurrentGameState) === EGameStates.POST_WIN_STATE) return;
        this.resumeBackgroundSound();
    }

    private onIdle(): void {
        const { currentState: currentWinPresentationState } = gameInstance.gameStore.get(EGameStoreKeys.WinPresentation);
        if (
            currentWinPresentationState !== EWinPresentationStates.WIN_PRESENTATION_HIDDEN_STATE ||
            this.manager.isPlaying(BackgroundSoundManager.START_UP_SOUND_KEY)
        )
            return;
        this.resumeBackgroundSound();
    }

    private onSpin(): void {
        this.resumeBackgroundSound();
    }

    private getBackgroundSoundKey(): string {
        let backgroundSoundKey: string = BackgroundSoundManager.BACKGROUND_SOUND_KEY;

        if (this.gameStore.get(EGameStoreKeys.CurrentSpecialGamesType)) {
            backgroundSoundKey =
                this.BACKGROUND_SOUND_KEY_BY_GAME_TYPE.get(this.gameStore.get(EGameStoreKeys.CurrentSpecialGamesType)!) ??
                BackgroundSoundManager.BACKGROUND_SOUND_KEY;
        }

        return backgroundSoundKey;
    }

    private onPreSpecialGamesIntroState() {
        this.pauseBackgroundSound();
    }

    private onPostSpecialGamesIntroState() {
        this.updateBackgroundMusic();
    }

    private onPostSpecialGamesOutroState() {
        this.updateBackgroundMusic();
    }

    private updateBackgroundMusic() {
        const newBackgroundSoundKey = this.getBackgroundSoundKey();
        if (this.currentBackgroundSoundKey === newBackgroundSoundKey) return;
        this.setBackgroundSoundKey(newBackgroundSoundKey);
    }

    private setBackgroundSoundKey(newBackgroundSoundKey: string) {
        const isCurrentlyPlaying = this.manager.isPlaying(this.currentBackgroundSoundKey);
        this.manager.stopSound(this.currentBackgroundSoundKey);
        this.manager.playSound(newBackgroundSoundKey, { loop: true });
        if (!isCurrentlyPlaying) this.manager.pauseSound(newBackgroundSoundKey);
        this.currentBackgroundSoundKey = newBackgroundSoundKey;
    }
}
